import { gql } from '@apollo/client';

/**
 * Profile Module GraphQL Queries and Mutations
 *
 * Centralized GraphQL operations for profile management
 */

// ─── SHIPPING ADDRESS FRAGMENT ──────────────────────────────────────────
const SHIPPING_ADDRESS_FIELDS = gql`
  fragment ShippingAddressFields on ClientShippingDetails {
    name
    phone
    address {
      street
      city
      state
      postcode
      country
    }
    isDefault
    instructions
  }
`;

// ─── PREFERENCES FRAGMENT ───────────────────────────────────────────────
const PREFERENCES_FIELDS = gql`
  fragment PreferencesFields on ClientPreferences {
    emailNotifications
    smsNotifications
    marketingEmails
    securityAlerts
    language
    timezone
    currency
    twoFactorEnabled
    sessionTimeout
  }
`;

// ─── CLIENT QUERIES ─────────────────────────────────────────────────────

export const GET_CLIENT = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      role
      isVerifiedSeller
      profilePhoto
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
      }
    }
  }
`;

/** Full client query for ViewProfile (includes addresses, permissions, preferences) */
export const GET_CLIENT_FULL = gql`
  ${SHIPPING_ADDRESS_FIELDS}
  ${PREFERENCES_FIELDS}
  query GetClientFull($id: ID!) {
    client(id: $id) {
      id
      tenantId
      createdAt
      updatedAt
      fName
      lName
      email
      phoneNumber
      role
      isVerifiedSeller
      businessName
      businessRegistrationNumber
      profilePhoto
      shippingAddresses {
        ...ShippingAddressFields
      }
      billingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
      }
      preferences {
        ...PreferencesFields
      }
      permissions
    }
  }
`;

/** Client query for Settings page (includes preferences) */
export const GET_CLIENT_SETTINGS = gql`
  ${PREFERENCES_FIELDS}
  query GetClientSettings($id: ID!) {
    client(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      permissions
      createdAt
      preferences {
        ...PreferencesFields
      }
    }
  }
`;

/** Client query for ShippingAddresses page */
export const GET_CLIENT_SHIPPING = gql`
  ${SHIPPING_ADDRESS_FIELDS}
  query GetClientShipping($id: ID!) {
    client(id: $id) {
      id
      shippingAddresses {
        ...ShippingAddressFields
      }
    }
  }
`;

// ─── CLIENT MUTATIONS ───────────────────────────────────────────────────

export const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      role
      isVerifiedSeller
      profilePhoto
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
      }
    }
  }
`;

/** Update shipping addresses (replaces entire array) */
export const UPDATE_CLIENT_SHIPPING = gql`
  ${SHIPPING_ADDRESS_FIELDS}
  mutation UpdateClientShipping($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      shippingAddresses {
        ...ShippingAddressFields
      }
    }
  }
`;

/** Add a single shipping address (dedicated mutation) */
export const ADD_CLIENT_SHIPPING_ADDRESS = gql`
  ${SHIPPING_ADDRESS_FIELDS}
  mutation AddClientShippingAddress($input: ClientShippingDetailsInput!) {
    addClientShippingAddress(input: $input) {
      id
      shippingAddresses {
        ...ShippingAddressFields
      }
    }
  }
`;

/** Update client preferences/settings */
export const UPDATE_CLIENT_PREFERENCES = gql`
  ${PREFERENCES_FIELDS}
  mutation UpdateClientPreferences($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      preferences {
        ...PreferencesFields
      }
    }
  }
`;

// ─── TENANT QUERIES ─────────────────────────────────────────────────────

export const GET_TENANT_BY_CURRENT_CLIENT = gql`
  query TenantByCurrentClient {
    tenantByCurrentClient {
      id
      name
      domain
      websiteUrl
      status
      subscriptionTier
      apiKeys {
        postmarkApiKey
        cellcastApiKey
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
      branding {
        siteName
        tagline
        description
        logo
        favicon
        primaryColor
        secondaryColor
        accentColor
      }
      emailConfig {
        fromEmail
        fromName
        replyToEmail
      }
      smsConfig {
        defaultSender
        defaultSenderType
        defaultTags
        defaultList
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      name
      domain
      websiteUrl
      apiKeys {
        postmarkApiKey
        cellcastApiKey
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
      branding {
        siteName
        tagline
        description
        logo
        favicon
        primaryColor
        secondaryColor
        accentColor
      }
      emailConfig {
        fromEmail
        fromName
        replyToEmail
      }
      smsConfig {
        defaultSender
        defaultSenderType
        defaultTags
        defaultList
      }
      updatedAt
    }
  }
`;

// ─── FILE UPLOAD ────────────────────────────────────────────────────────

export const UPLOAD_UNENCRYPTED_FILE = gql`
  mutation UploadUnencrypted($file: Upload!) {
    uploadUnencryptedFile(file: $file)
  }
`;

// ─── SUBSCRIPTION QUERIES ───────────────────────────────────────────────

export const GET_MY_SUBSCRIPTIONS = gql`
  query GetMySubscriptions {
    mySubscriptions {
      id
      plan {
        name
        description
        amount
        interval
        tier
      }
      status
      nextBillingDate
      canceledAt
      pausedAt
      createdAt
      stripeSubscriptionId
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($subscriptionId: ID!, $reason: String) {
    cancelSubscription(subscriptionId: $subscriptionId, reason: $reason) {
      id
      status
      canceledAt
    }
  }
`;

export const PAUSE_SUBSCRIPTION = gql`
  mutation PauseSubscription($subscriptionId: ID!) {
    pauseSubscription(subscriptionId: $subscriptionId) {
      id
      status
      pausedAt
    }
  }
`;

export const RESUME_SUBSCRIPTION = gql`
  mutation ResumeSubscription($subscriptionId: ID!) {
    resumeSubscription(subscriptionId: $subscriptionId) {
      id
      status
      pausedAt
    }
  }
`;

// ─── SUBSCRIPTION OFFERS QUERIES ────────────────────────────────────────

export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant($id: ID!) {
    tenant(id: $id) {
      id
      subscriptionTier
      stripeCustomerId
    }
  }
`;

export const UPDATE_TENANT_TIER = gql`
  mutation UpdateTenantTier($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      subscriptionTier
    }
  }
`;

export const GET_SUBSCRIPTION_PRODUCTS = gql`
  query PublicSubscriptionProducts($tenantId: String!) {
    publicSubscriptionProducts(tenantId: $tenantId) {
      id
      name
      slug
      description
      detailedDescription
      amount
      interval
      currency
      features
      badge
      color
      isRecommended
      sortOrder
      isActive
      tier
      includedModules
      ctaText
      trialDays
    }
  }
`;

// ─── PAYMENT METHOD QUERIES ─────────────────────────────────────────────

export const MY_PAYMENT_METHODS = gql`
  query MyPaymentMethods {
    myPaymentMethods {
      id
      type
      isDefault
      status
      stripePaymentMethodId
      stripeCustomerId
      cardDetails {
        brand
        last4
        expMonth
        expYear
      }
      description
      lastUsedAt
      createdAt
    }
  }
`;

export const GET_TENANT_STRIPE_PUBLIC_KEY = gql`
  query GetTenantStripePublicKey {
    getTenantStripePublicKey
  }
`;

export const CREATE_SETUP_INTENT = gql`
  mutation CreateSetupIntent($clientId: String!) {
    createSetupIntent(clientId: $clientId)
  }
`;

export const CREATE_PAYMENT_METHOD = gql`
  mutation CreatePaymentMethod($input: PaymentMethodInput!) {
    createPaymentMethod(input: $input) {
      id
      type
      status
      isDefault
      stripePaymentMethodId
      stripeCustomerId
      cardDetails {
        brand
        last4
        expMonth
        expYear
      }
      createdAt
    }
  }
`;

export const DELETE_PAYMENT_METHOD = gql`
  mutation DeletePaymentMethod($id: ID!) {
    deletePaymentMethod(id: $id)
  }
`;

export const SET_DEFAULT_PAYMENT_METHOD = gql`
  mutation SetDefaultPaymentMethod($id: ID!) {
    setDefaultPaymentMethod(id: $id)
  }
`;
